/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {List, makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import isNil from 'lodash.isnil';
import {hasHideViewEventCTA} from '@ion/applications/src/pages/admin/notifications/NotificationUtils';
import {translate} from '@ion/core/src/locale/locale';
import {AlertSeverity} from '@ion/core/src/types/entities';
import Accordion from '@ion/dls/src/components/accordion/Accordion';
import {IonSpinner} from '@ion/dls/src/components/loading/IonSpinner';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {EmptyNotifications} from '@ion/dls/src/notifications/EmptyNotifications';
import {categorizeNotificationByDate} from '@ion/dls/src/notifications/helpers';
import {NotificationCard} from '@ion/dls/src/notifications/NotificationCard';
import {TimeStamp} from '@ion/dls/src/notifications/types';
import Render from '@ion/dls/src/render/Render';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {Notification} from '@ion/dls/src/types/extensions';
import {DLSTheme} from '@ion/dls/src/types/types';
import {hasHistoryAccess, hasTrackingPageAccess} from '@/security/Features';
import {
  getNotificationDetails,
  markAsCleared,
  markAsRead,
  selectNotification,
  setNotificationPanelState
} from '@/store/actions/notifications';
import ViewEventCta from './ViewEventCta';

export interface NotificationPanelContentProps {
  checkboxesEnabled?: boolean;
  filterUnread?: boolean;
  isCheckAll?: boolean;
  onReviewClick?: (notification: Notification) => void;
  currentSelectedNotifications: Notification[];
  setCurrentSelectedNotifications: Function;
};

interface StyleProps {
  count?: number;
  isPhone?: boolean;
  isCurrentTheme?: boolean;
};

const panelHeight            = '83vh';
const panelHeightMobile      = 'calc(100vh - 107px)';
const mobileHeightAdjustment = 50;

// legacy theme's original fixed lookup table - kept as-is since the
// formula below is calibrated to the current theme's own (different)
// collapsed-accordion height, verified only for the current theme ...
const legacyPanelHeightMap = {
  1 : 164,
  2 : 214,
  3 : 264,
  4 : 314
};

// height of one COLLAPSED accordion's own container in the CURRENT theme
// (DOM-measured via DevTools) - only the currently expanded accordion's
// list actually needs height budgeted for it, every other accordion in
// the group just contributes this fixed header height ...
const accordionHeaderHeight = 56;

// height of everything else above the accordion list (drawer header,
// tabs, option bar, etc.) that doesn't scale with accordion count -
// derived from the verified count=2 case (1 expanded + 1 collapsed):
// 339 total - (1 collapsed accordion * 56) = 283 ...
const notificationListBaseHeight = 283;

const panelHeightCalc = ({count, isPhone, isCurrentTheme}: StyleProps) => {
  if (isCurrentTheme) {
    const subHeight = notificationListBaseHeight +
      ((count - 1) * accordionHeaderHeight) +
      (isPhone ? mobileHeightAdjustment : 0);
    return `calc(100vh - ${subHeight}px)`;
  }

  const subHeight = legacyPanelHeightMap[count] + (isPhone ? mobileHeightAdjustment : 0);
  return subHeight ? `calc(100vh - ${subHeight}px)` : panelHeight;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  notificationPanelList : {
    overflowX     : 'hidden',
    overflowY     : 'auto',
    paddingBottom : '0px'
  },
  currentMobileNotificationPanelList : {
    maxHeight : 'calc(-264px + 100vh) !important',
    height    : 'calc(-338px + 100vh) !important'
  },
  loader : {
    height         : 'calc(100vh - 375px)',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  accordionTitle : toggleThemeStyle({
    ...dlsTypography('panelCards'),
    fontFamily    : 'Roboto',
    fontWeight    : '600',
    fontSize      : '14px',
    lineHeight    : '17px',
    textTransform : 'uppercase',
    color         : dlsColor('black'),
    minHeight     : 48
  }, {
    color          : dlsColor('grey2'),
    display        : 'flex',
    fontSize       : '16px',
    alignItems     : 'center',
    fontFamily     : 'Roboto',
    fontWeight     : 700,
    lineHeight     : '18px',
    letterSpacing  : '1px',
    justifyContent : 'center'
  }),
  accordion : {
    padding : '0 15px'
  },
  notificationsWrapper : {
    gap             : '4px',
    width           : '24px',
    height          : '24px',
    margin          : '0 8px',
    display         : 'flex',
    alignItems      : 'center',
    borderRadius    : '4px',
    justifyContent  : 'center',
    backgroundColor : dlsColor('aquamarine')
  },
  notificationTitle : {
    color         : dlsColor('darkTeal'),
    fontSize      : '12px',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    lineHeight    : '16px',
    letterSpacing : '1px'
  }
}));

/**
 * @function NotificationPanelContent
 * @returns Node
 *
 * Component that houses the contents of the notification
 * drawer that is opened when clicking the icon in the header
 */
const NotificationPanelContent = ({
  checkboxesEnabled = false,
  filterUnread = false,
  isCheckAll = false,
  onReviewClick,
  currentSelectedNotifications,
  setCurrentSelectedNotifications
}: NotificationPanelContentProps) => {
  const dispatch                = useDispatch();
  const {isPhone}               = useMedia();
  const activeTab               = useSelector(({notifications}) => notifications.panel.activePanel);
  const detailsState            = useSelector(({notifications}) => notifications.panel.state);
  const selectedNotifications   = useSelector(({notifications}) => notifications.panel.selected);
  const [selected, setSelected] = useState(TimeStamp.TODAY);

  const {
    all: allNotifications,
    urgent : urgentNotifications,
    loading: isLoading
  } = useSelector(({notifications}) => notifications);

  const [
    activeNotifications,
    setActiveNotifications
  ] = useState(null);

  const {
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const accordionKeys = Object.keys(activeNotifications || {});
  const classes       = useStyles();

  const unreadAllNotifications    = [...allNotifications].filter(notification => !notification.isRead);
  const unreadUrgentNotifications = [...urgentNotifications].filter(
    notification => !notification.isRead
  );

  useEffect(() => {
    const selectedUuids = new Set(selectedNotifications.map(active => active.uuid));
    const selected = accordionKeys.flatMap(accordion =>
      activeNotifications[accordion].filter(notification => selectedUuids.has(notification.uuid))
    );
    setCurrentSelectedNotifications(selected);
  }, [selectedNotifications?.toString(), activeNotifications?.toString(), accordionKeys.toString()]);

  // since the urgent tab's value is 0 we can use a truthy/falsy check
  const emptyTitle = activeTab ? translate('notifications:panel.emptyAll') : translate(`notifications:panel.${isCurrentTheme ? 'currentEmptyUrgent' : 'emptyUrgent'}`);

  useEffect(() => {
    if (isLoading) {
      setActiveNotifications(null);
    }
  }, [isLoading]);

  useEffect(() => {
    if (accordionKeys?.length) {
      setSelected(accordionKeys[0] as TimeStamp);
    }
  }, [accordionKeys.toString()]);

  useEffect(() => {
    const notifications = categorizeNotificationByDate(activeTab ? allNotifications : urgentNotifications);
    setActiveNotifications(notifications);
  }, [allNotifications, urgentNotifications, activeTab]);

  useEffect(() => {
    if (activeTab) {
      const notifications = categorizeNotificationByDate(
        filterUnread ? unreadAllNotifications : allNotifications
      );
      setActiveNotifications(notifications);
    } else {
      const notifications = categorizeNotificationByDate(
        filterUnread ? unreadUrgentNotifications : urgentNotifications
      );
      setActiveNotifications(notifications);
    }
  }, [filterUnread, activeTab, allNotifications, urgentNotifications]);

  function readMore(notification: Notification) {
    const {uuid} = notification;
    const temp = [...detailsState];
    let updatedTemp;
    const matchingIndex = detailsState.findIndex(item => item.id === uuid);
    if (matchingIndex > -1) {
      temp[matchingIndex] = {id: uuid, open: true, loading: !notification?.details};
      updatedTemp = temp;
      dispatch(setNotificationPanelState(updatedTemp));
    } else {
      dispatch(setNotificationPanelState(
        [...detailsState, {id: uuid, open: true, loading: !notification?.details}]
      ));
    }
    if (!notification?.details) {
      dispatch(getNotificationDetails(notification) as any);
    }
  }

  function readLess(notification: Notification) {
    const {uuid} = notification;
    const temp = [...detailsState];
    let updatedTemp;
    const matchingIndex = detailsState.findIndex(item => item.id === uuid);
    if (matchingIndex > -1) {
      temp[matchingIndex] = {id: uuid, open: false, loading: false};
      updatedTemp = temp;
      dispatch(setNotificationPanelState(updatedTemp));
    } else {
      dispatch(setNotificationPanelState(
        [...detailsState, {id: uuid, open: false, loading: false}]
      ));
    }
  }

  function markNotificationRead(notification: Notification) {
    if (!notification?.isRead) dispatch(markAsRead(notification) as any);
  }

  function onClearClick(notification) {
    dispatch(markAsCleared(notification) as any);
  }

  function onCardClick(notification, cardSelected) {
    dispatch(selectNotification(cardSelected, notification) as any);
  }

  function handleCheckBoxChange(checked, notification) {
    dispatch(selectNotification(checked, notification) as any);
  }

  return (
    <>
      {
        !isLoading && !isNil(activeNotifications) ? (
          accordionKeys.length ? (
            accordionKeys.map(accordion => (
              <div className={classes.accordionWrapper}>
                <Accordion className={classes.accordion}
                  expanded={accordion === selected}
                  key={accordion}
                  onExpand={function(expand: boolean) {
                    setSelected(expand ? accordion as TimeStamp : null);
                  }}
                  header={
                    <>
                      <div className={classes.accordionTitle}>
                        {translate(`notifications:panel.accordionTitle.${accordion.toLowerCase()}`)}
                        <Render.If condition={!isCurrentTheme}>
                          ({activeNotifications[accordion].length})
                        </Render.If>
                      </div>
                      <Render.If condition={isCurrentTheme}>
                        <div className={classes.notificationsWrapper}>
                          <span className={classes.notificationTitle}>
                            {activeNotifications[accordion].length}
                          </span>
                        </div>
                      </Render.If>
                    </>
                  }
                  details={
                    <List className={classnames(classes.notificationPanelList, {
                      [classes.currentMobileNotificationPanelList] : isCurrentTheme && isPhone
                    })} style={{
                      maxHeight : panelHeightCalc({
                        count : (accordionKeys?.length ?? 0) + (currentSelectedNotifications?.length ? 1 : 0),
                        isPhone,
                        isCurrentTheme
                      })
                    }}>
                      {activeNotifications[accordion]?.map((notification: Notification, i: number) => {
                        const {
                          category,
                          isRead,
                          alertConfigurationName,
                          severity = AlertSeverity.MEDIUM,
                          createdOn,
                          uuid,
                          assetName,
                          alertType
                        } = notification;
                        const {title: alertTypeTitle} = alertType;
                        const matchingClick = detailsState.find(clicked => clicked.id === uuid);
                        const isDetailsOpen = matchingClick?.open && !!notification?.details;
                        const isLoadingDetails = matchingClick?.loading;
                        const isChecked = selectedNotifications.find(active => active.uuid === uuid);
                        const isUrgent = severity === AlertSeverity.CRITICAL;
                        const hasEventAccess = hasHistoryAccess();
                        const isHideViewEventCta = !hasEventAccess || hasHideViewEventCTA(alertTypeTitle);
                        return (
                          <div key={`notification-card-${uuid}`}>
                            <NotificationCard type={alertTypeTitle}
                              text={assetName}
                              subText={alertConfigurationName}
                              read={isRead}
                              hasAccessToEntiry={hasTrackingPageAccess()}
                              timeStamp={createdOn}
                              notification={notification}
                              isHideViewEventCta={isHideViewEventCta}
                              isUrgent={isUrgent}
                              viewEventCta={
                                <ViewEventCta notification={notification} />
                              }
                              onMoreClick={readMore}
                              onLessClick={readLess}
                              moreInfoLoading={isLoadingDetails}
                              showMoreInfo={isDetailsOpen}
                              showCheckBox={checkboxesEnabled}
                              isCheckAll={isCheckAll}
                              onCardClick={onCardClick}
                              onClearClick={onClearClick}
                              onReviewClick={onReviewClick}
                              onCheckBoxChange={handleCheckBoxChange}
                              isChecked={!!isChecked}
                              disableSingleClear={!!currentSelectedNotifications?.length} />
                          </div>
                        );
                      })
                      }
                    </List>
                  }
                  borderBottom='none'
                  padding='0px 0'
                  isCondensedVariant />
              </div>
            ))) : (
            <EmptyNotifications title={emptyTitle}
              desktopHeight={isCurrentTheme ? panelHeightCalc({count: 0, isPhone, isCurrentTheme}) : panelHeight}
              mobileHeight={panelHeightMobile} />
          )
        ): (
          <div className={classes.loader}>
            <IonSpinner />
          </div>
        )
      }
    </>
  );
};

export default NotificationPanelContent;
